import { useTheme } from "#components/theme-provider"
import { usePrivacy } from "#contexts/PrivacyContext"
import { useCurrencyFormatters } from "#hooks/use-currency-formatters"
import { motion } from "framer-motion"
import { Layer, Text } from "recharts"

// Custom link component to render links with gradient colors and labels
// Recharts Sankey passes link props - we need to render the actual path
export const SankeyLink = (props: any) => {
  const { sourceX, sourceY, targetX, targetY, sourceControlX, targetControlX, linkWidth, payload } =
    props
  const { resolvedTheme } = useTheme()
  const isDark = resolvedTheme === "dark"
  const { isBlurred } = usePrivacy()
  const { formatCurrencyWithDecimals } = useCurrencyFormatters()

  // Recharts passes the link data in payload
  const linkData = payload?.payload?.payload || payload?.payload || payload
  const sourceNode = linkData?.source
  const targetNode = linkData?.target

  if (!linkData?.value || !linkWidth || linkWidth <= 0) {
    return null
  }

  // Get colors from nodes
  const sourceColor = sourceNode?.color || "#3b82f6"
  const targetColor = targetNode?.color || "#94a3b8"

  // Create gradient ID unique to this link
  const gradientId = `link-gradient-${props.index || 0}`

  // For Sankey links, we need to account for the link width at source and target
  // The y positions need to be adjusted based on linkWidth
  const sourceYTop = sourceY - linkWidth / 2
  const sourceYBottom = sourceY + linkWidth / 2
  const targetYTop = targetY - linkWidth / 2
  const targetYBottom = targetY + linkWidth / 2

  // Calculate control points for smooth curves
  const dx = targetX - sourceX
  const controlX1 = sourceControlX ?? sourceX + dx * 0.5
  const controlX2 = targetControlX ?? sourceX + dx * 0.5

  // Build the curved path - Sankey links are typically rendered as filled paths
  // We need to create a closed path that forms a curved band
  const path = `
      M ${sourceX},${sourceYTop}
      C ${controlX1},${sourceYTop} ${controlX2},${targetYTop} ${targetX},${targetYTop}
      L ${targetX},${targetYBottom}
      C ${controlX2},${targetYBottom} ${controlX1},${sourceYBottom} ${sourceX},${sourceYBottom}
      Z
  `

  // Calculate midpoint for label - positioned along the curve
  const midX = (sourceX + targetX) / 2
  const midY = (sourceY + targetY) / 2

  // Calculate percentages
  const partnerPercentage = linkData.percentage || 0 // Percentage of partner's total spending
  const categoryPercentage =
    targetNode?.value && targetNode.value > 0 ? (linkData.value / targetNode.value) * 100 : 0 // Percentage of category's total income

  // Format currency value (0 decimal places for link labels)
  const formatCurrencyValue = (value: number) => formatCurrencyWithDecimals(value, 0, 0)

  // Determine if link is wide enough to show labels
  const minWidthForLabel = 10
  const showLabels = linkWidth >= minWidthForLabel

  // Smart label placement: offset slightly above or below the link center
  // to avoid overlap with other links
  const labelOffsetY = linkWidth < 20 ? -linkWidth / 2 - 8 : 0

  // Build unified label text: {amount} € ({partnerPct}%, {categoryPct}%)
  const percentageParts: string[] = []
  if (partnerPercentage > 0) {
    percentageParts.push(`${partnerPercentage.toFixed(0)}%`)
  }
  if (categoryPercentage > 0) {
    percentageParts.push(`${categoryPercentage.toFixed(0)}%`)
  }
  const unifiedLabel =
    percentageParts.length > 0
      ? `${formatCurrencyValue(linkData.value)} (${percentageParts.join(", ")})`
      : formatCurrencyValue(linkData.value)

  // Automatic label collision management: adjust font size based on link width
  // Start with base size and reduce if needed
  let labelFontSize = Math.max(10, Math.min(12, linkWidth / 2.5))
  // If link is narrow, reduce font size further
  if (linkWidth < 16) {
    labelFontSize = Math.max(9, Math.min(10, linkWidth / 3))
  }
  // If link is very narrow, hide percentages
  const showPercentages = linkWidth >= 14 && percentageParts.length > 0

  return (
    <Layer key={`CustomLink${props.index || 0}`}>
      {/* Define gradient for this link */}
      <defs>
        <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor={sourceColor} stopOpacity={0.7} />
          <stop offset="100%" stopColor={targetColor} stopOpacity={0.7} />
        </linearGradient>
      </defs>
      {/* Render the link path with gradient fill and stroke outline */}
      <motion.path
        d={path}
        fill={`url(#${gradientId})`}
        stroke="rgba(0,0,0,0.25)"
        strokeWidth={0.75}
        opacity={0.7}
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.7 }}
        transition={{ duration: 0.7, ease: "easeOut" }}
      />
      {/* Render unified label if link is wide enough */}
      {showLabels && (
        <Text
          x={midX}
          y={midY + labelOffsetY}
          textAnchor="middle"
          verticalAnchor="middle"
          fontSize={labelFontSize}
          fill={isDark ? "#f9fafb" : "#1f2937"}
          fontWeight={600}
          style={{
            pointerEvents: "none",
            filter: isBlurred ? "blur(4px)" : "none",
          }}
        >
          {showPercentages ? unifiedLabel : formatCurrencyValue(linkData.value)}
        </Text>
      )}
    </Layer>
  )
}
